import { useCallback, useState } from 'react';

import FormDialog from '@sayyesschool/frontend/components/form-dialog';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import { useMeetings } from '@sayyesschool/frontend/features/meetings';
import { useTeachers } from '@sayyesschool/frontend/features/teachers';

import MeetingForm from '@/components/meetings/meeting-form';
import MeetingsTable from '@/components/meetings/meetings-table';

const filters = {
    all: () => true,
    upcoming: meeting => !meeting.isEnded,
    ended: meeting => meeting.isEnded
};

export default function MeetingsPage() {
    const [meetings, actions] = useMeetings();
    const [teachers] = useTeachers();

    const [tab, setTab] = useState('upcoming');
    const [meeting, setMeeting] = useState();
    const [isLoading, setLoading] = useState(false);
    const [isFormOpen, setFormOpen] = useState(false);

    const handleSubmit = useCallback(data => {
        setLoading(true);

        const request = meeting?.id
            ? actions.updateMeeting(meeting.id, data)
            : actions.createMeeting(data);

        return request
            .then(() => setFormOpen(false))
            .finally(() => setLoading(false));
    }, [actions, meeting]);

    const handlePublish = useCallback(meeting => {
        setLoading(true);

        return actions.updateMeeting(meeting.id, { published: !meeting.published })
            .finally(() => setLoading(false));
    }, [actions]);

    const handleEdit = useCallback(meeting => {
        setMeeting(meeting);
        setFormOpen(true);
    }, []);

    const handleDelete = useCallback(meeting => {
        setLoading(true);

        return actions.deleteMeeting(meeting.id).finally(() => setLoading(false));
    }, [actions]);

    const handleFormClose = useCallback(() => {
        setMeeting();
        setFormOpen(false);
    }, []);

    if (!meetings) return <LoadingIndicator />;

    const filteredMeetings = meetings
        .filter(filters[tab])
        .sort((a, b) => tab === 'ended'
            ? new Date(b.date) - new Date(a.date)
            : new Date(a.date) - new Date(b.date)
        );

    return (
        <Page>
            <Page.Header
                title="Встречи"
                tabs={{
                    value: tab,
                    items: [
                        { key: 'upcoming', value: 'upcoming', content: 'Предстоящие' },
                        { key: 'ended', value: 'ended', content: 'Прошедшие' },
                        { key: 'all', value: 'all', content: 'Все' }
                    ],
                    onChange: (_, value) => setTab(value)
                }}
                actions={[{
                    key: 'add',
                    icon: 'add',
                    content: 'Создать встречу',
                    variant: 'solid',
                    onClick: () => setFormOpen(true)
                }]}
            />

            <Page.Content>
                <Page.Section
                    variant="outlined"
                    compact
                    scrollable
                >
                    <MeetingsTable
                        meetings={filteredMeetings}
                        onEdit={handleEdit}
                        onPublish={handlePublish}
                        onDelete={handleDelete}
                    />
                </Page.Section>
            </Page.Content>

            <FormDialog
                title={meeting?.id ? 'Изменить встречу' : 'Новая встреча'}
                open={isFormOpen}
                onClose={handleFormClose}
            >
                <MeetingForm
                    id="meeting-form"
                    meeting={meeting}
                    hosts={teachers}
                    onSubmit={handleSubmit}
                />
            </FormDialog>
        </Page>
    );
}